import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from './auth.js';
import { apiJson, UnauthorizedError, HttpError } from './api-client.js';

// 502/503/504 from the API are almost always transient — a Lambda mid-deploy
// or a brief integration blip. Retrying with a short backoff hides them
// instead of dumping a raw "HTTP 503" into the UI. A definitive 4xx/500 keeps
// the prior single-retry behavior, and an auth-expiry (UnauthorizedError) is
// never retried — the refresh path re-runs the query via its queryKey instead.
const TRANSIENT_STATUSES = new Set([502, 503, 504]);

export function apiQueryRetry(failureCount: number, error: unknown): boolean {
  if (error instanceof UnauthorizedError) return false;
  if (error instanceof HttpError && TRANSIENT_STATUSES.has(error.status)) return failureCount < 3;
  return failureCount < 1;
}

export function apiRetryDelay(attemptIndex: number): number {
  return Math.min(500 * 2 ** attemptIndex, 8_000);
}

export function useApiQuery<T>(
  key: unknown[],
  path: string,
  opts?: { enabled?: boolean; refetchInterval?: Parameters<typeof useQuery<T>>[0]['refetchInterval'] },
) {
  const { idToken, refreshSession } = useAuth();
  const query = useQuery<T>({
    queryKey: [...key, idToken],
    queryFn: () => apiJson<T>(path, idToken!),
    enabled: !!idToken && (opts?.enabled ?? true),
    retry: apiQueryRetry,
    retryDelay: apiRetryDelay,
    ...(opts?.refetchInterval !== undefined ? { refetchInterval: opts.refetchInterval } : {}),
  });
  // A 401 from the API is *not* a sign-out signal — it's a "your id_token
  // didn't authorize" signal, which happens routinely on cold-start Lambdas
  // right after a deploy. refreshSession() exchanges the stored refresh_token
  // for a fresh id_token; gated queries automatically re-run because idToken
  // is in their queryKey. Only if the refresh itself fails does the in-memory
  // session clear — and even then the 7-day refresh_token survives in
  // localStorage for the next attempt.
  useEffect(() => {
    if (query.error instanceof UnauthorizedError) void refreshSession();
  }, [query.error, refreshSession]);
  return query;
}
